<script setup lang="ts">
import { useDisplay } from 'vuetify'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useCitizenInquiriesStore } from '../../stores/citizenInquiriesStore'
import type { IAppealChannel } from '../../types/IAppeal'

const props = defineProps<{
  modelValue: boolean
  channel?: IAppealChannel | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  saved: []
}>()

const { smAndDown } = useDisplay()
const store = useCitizenInquiriesStore()

const name = ref('')
const initialName = ref('')
const saving = ref(false)
const showUnsavedChangesDialog = ref(false)
const error = ref('')

const isEditMode = computed(() => Boolean(props.channel))
const title = computed(() => isEditMode.value ? 'Переименование канала' : 'Создание канала')

watch(
  () => props.modelValue,
  isOpen => {
    error.value = ''
    if (!isOpen) return
    name.value = props.channel?.name || ''
    initialName.value = name.value.trim()
  },
)

function closeModalDirectly() {
  emit('update:modelValue', false)
  error.value = ''
}

function handleCloseModal() {
  if (name.value.trim() !== initialName.value)
    showUnsavedChangesDialog.value = true
  else
    closeModalDirectly()
}

const onDialogDismissAttempt = useDialogDismissGuard(handleCloseModal)

function handleCloseConfirm(confirmed: boolean) {
  showUnsavedChangesDialog.value = false
  if (confirmed)
    closeModalDirectly()
}

async function handleSave() {
  const trimmed = name.value.trim()
  if (!trimmed) {
    error.value = 'Наименование канала обязательно для заполнения'
    return
  }
  if (isEditMode.value && trimmed === initialName.value) {
    closeModalDirectly()
    return
  }

  saving.value = true
  try {
    if (props.channel)
      await store.updateChannel(props.channel.id, { name: trimmed })
    else
      await store.createChannel({ name: trimmed })
    emit('saved')
    closeModalDirectly()
  } catch {
    // Ошибка уже показана стором
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="600"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard>
      <div class="d-flex align-center justify-space-between pa-6">
        <span class="text-h4 appeal-channel-modal-title">{{ title }}</span>
        <CustomCloseButton :close-action="onDialogDismissAttempt" />
      </div>

      <VCardText>
        <div class="d-flex flex-column gap-5 px-0">
          <VTextField
            v-model="name"
            placeholder="Например: Телефон, Личный приём, Письменное обращение"
            variant="outlined"
            clearable
            persistent-placeholder
            autofocus
            :error="!!error"
            :error-messages="error"
            @click:clear="name = ''"
            @update:model-value="error = ''"
            @keydown.enter.prevent="handleSave"
          >
            <template #label>
              <span>Наименование канала<sup class="required-label">*</sup></span>
            </template>
          </VTextField>

          <div class="d-flex align-center" :class="smAndDown ? 'flex-column' : 'justify-end'">
            <VBtn
              :block="smAndDown"
              color="primary"
              :loading="saving"
              @click="handleSave"
            >
              {{ isEditMode ? 'Сохранить' : 'Создать' }}
            </VBtn>
          </div>
        </div>
      </VCardText>
    </VCard>

    <ConfirmDialog
      v-model:is-dialog-visible="showUnsavedChangesDialog"
      confirmation-question="У вас есть несохраненные изменения. Вы уверены, что хотите закрыть окно?"
      confirm-btn="Закрыть"
      reject-btn="Остаться"
      @confirm="handleCloseConfirm"
    />
  </VDialog>
</template>

<style scoped lang="scss">
.appeal-channel-modal-title {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
}
</style>
